import { type ReactNode, createContext, useContext, useState } from 'react';

import { CONFIG } from '@carboncopyco/components';
import type { DeviceType } from '@carboncopyco/types';

type DimensionsContextType = {
  type: DeviceType;
  setType: (type: DeviceType) => void;
  appliedType: DeviceType;
  setAppliedType: (type: DeviceType) => void;
  width: number;
  height: number;
  setWidth: (width: number) => void;
  setHeight: (height: number) => void;
  appliedWidth: number;
  appliedHeight: number;
  setAppliedWidth: (width: number) => void;
  setAppliedHeight: (height: number) => void;
  isSelected: boolean;
  setIsSelected: (isSelected: boolean) => void;
};

const defaultWidth = CONFIG.desktop.dimension.width;
const defaultHeight = CONFIG.desktop.dimension.height;
const contextDefaultValue: DimensionsContextType = {
  type: 'desktop',
  setType: () => {},
  width: defaultWidth,
  height: defaultHeight,
  setWidth: () => {},
  setHeight: () => {},
  appliedType: 'desktop',
  setAppliedType: () => {},
  appliedWidth: defaultWidth,
  appliedHeight: defaultHeight,
  setAppliedWidth: () => {},
  setAppliedHeight: () => {},
  isSelected: false,
  setIsSelected: () => {},
};

const DimensionsContext =
  createContext<DimensionsContextType>(contextDefaultValue);

export const useDimensionsContext = () => {
  const context = useContext(DimensionsContext);

  if (!context) {
    throw new Error(
      'useResponsiveModeContext must be used within a ResponsiveModeContextProvider'
    );
  }

  return context;
};

export const DimensionsContextProvider = ({
  children,
  viewElement,
}: {
  children: ReactNode;
  viewElement: HTMLElement;
}) => {
  const [type, setType] = useState<DeviceType>('desktop');
  const [appliedType, setAppliedType] = useState<DeviceType>('desktop');
  const [width, setWidth] = useState(defaultWidth);
  const [height, setHeight] = useState(defaultHeight);
  const [appliedWidth, setAppliedWidth] = useState(defaultWidth);
  const [appliedHeight, setAppliedHeight] = useState(defaultHeight);
  const [isSelected, setIsSelected] = useState(false);

  return (
    <DimensionsContext.Provider
      value={{
        type,
        setType,
        appliedType,
        setAppliedType,
        width,
        height,
        setWidth,
        setHeight,
        appliedWidth,
        appliedHeight,
        setAppliedWidth,
        setAppliedHeight,
        isSelected,
        setIsSelected,
      }}
    >
      {children}
    </DimensionsContext.Provider>
  );
};
